
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Gamepad, Play, Star, Users, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import { createGameUrl } from '@/utils/gameUtils';
import AdDisplay from '@/components/ads/AdDisplay';

interface BlogRelatedGamesProps {
  postId: string;
}

// Custom wrapper component for ads that only renders when ads are available
const AdWrapper: React.FC<{ adKey: string }> = ({ adKey }) => {
  const { data: ads = [] } = useQuery({
    queryKey: ['ads', 'blog-related-games'],
    queryFn: async () => {
      // Use the same ad service logic to check if ads exist
      const { getAdsByPosition } = await import('@/utils/adService');
      return getAdsByPosition('blog-related-games');
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Only render if ads are available
  if (ads.length === 0) return null;

  return (
    <div key={adKey} className="w-full">
      <AdDisplay position="blog-related-games" />
    </div>
  );
};

const BlogRelatedGames: React.FC<BlogRelatedGamesProps> = ({ postId }) => {
  const { data: relatedGames, isLoading } = useQuery({
    queryKey: ['blog-related-games', postId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('blog_post_related_games')
        .select(`
          game_id,
          games:game_id (
            id,
            title,
            thumbnail,
            category,
            description,
            rating,
            play_count
          )
        `)
        .eq('blog_post_id', postId);
      
      if (error) throw error;
      return data.map(item => item.games).filter(Boolean);
    },
  });

  if (isLoading || !relatedGames || relatedGames.length === 0) {
    return null;
  }

  // Insert native ads after every 3rd game (not before the first game)
  const gamesWithAds = [];
  for (let i = 0; i < relatedGames.length; i++) {
    gamesWithAds.push(relatedGames[i]);
    
    // Add native ad after every 3rd game, but not after the last game
    if ((i + 1) % 3 === 0 && i < relatedGames.length - 1) {
      gamesWithAds.push({ isAd: true, adKey: `native-ad-${Math.floor(i / 3)}` });
    }
  }

  return (
    <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 via-blue-600 to-indigo-600 p-8">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center shadow-lg">
            <Gamepad className="w-7 h-7 text-white" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h2 className="text-2xl md:text-3xl font-bold text-white">Related Games</h2>
              <Zap className="w-5 h-5 text-white/80" />
            </div>
            <p className="text-purple-100 text-sm md:text-base">Try these amazing games mentioned in this article</p>
          </div>
        </div>
      </div>
      
      {/* Games Grid with Native Ads */}
      <div className="p-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {gamesWithAds.map((item: any, index) => 
            item.isAd ? 
              <AdWrapper key={item.adKey} adKey={item.adKey} /> :
              (
                // Game Card
                <Card key={item.id} className="group overflow-hidden hover:shadow-2xl transition-all duration-500 border-0 shadow-lg hover:-translate-y-2 h-full flex flex-col">
                  {/* Game Image */}
                  <div className="relative aspect-video overflow-hidden">
                    <img
                      src={item.thumbnail || '/placeholder.svg'}
                      alt={item.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    
                    {/* Stats Overlay */}
                    <div className="absolute bottom-3 left-3 right-3 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300 opacity-0 group-hover:opacity-100">
                      <div className="flex items-center justify-between text-white text-sm">
                        <div className="flex items-center gap-1 bg-black/40 backdrop-blur-sm rounded-full px-2 py-1">
                          <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                          <span className="font-medium">{(item.rating || 0).toFixed(1)}</span>
                        </div>
                        <div className="flex items-center gap-1 bg-black/40 backdrop-blur-sm rounded-full px-2 py-1">
                          <Users className="w-4 h-4" />
                          <span className="font-medium">{(item.play_count || 0).toLocaleString()}</span>
                        </div>
                      </div>
                    </div>

                    {/* Category Badge */}
                    <div className="absolute top-3 left-3">
                      <span className="bg-gradient-to-r from-purple-500 to-blue-500 text-white text-xs font-semibold px-3 py-1 rounded-full shadow-lg">
                        {item.category}
                      </span>
                    </div>
                  </div>
                  
                  {/* Game Info */}
                  <CardContent className="p-6 flex-1 flex flex-col">
                    <div className="space-y-3 flex-1">
                      <div>
                        <h3 className="font-bold text-lg text-gray-900 line-clamp-1 group-hover:text-purple-600 transition-colors">
                          {item.title}
                        </h3>
                      </div>
                      
                      <p className="text-sm text-gray-600 line-clamp-2 leading-relaxed flex-1">
                        {item.description || `Experience the exciting ${item.category} game ${item.title}. Play now for free and join thousands of players!`}
                      </p>
                    </div>
                    
                    {/* Play Button */}
                    <Button 
                      asChild 
                      className="w-full mt-4 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 border-0 shadow-lg hover:shadow-xl transition-all duration-300 group-hover:scale-105"
                    >
                      <Link to={createGameUrl(item.title)} className="flex items-center justify-center gap-2">
                        <Play className="w-4 h-4" />
                        <span className="font-semibold">Play Now</span>
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              )
          )}
        </div>
      </div>
    </div>
  );
};

export default BlogRelatedGames;
